import { CalculationMethod } from "constants/charts.enums";

import { MaxBinsSortAggregation, mBinSize } from "models/binSize";
import { EntityKind } from "models/entityKind";
import { IGroupByListItem } from "models/groupBy";

import { findCatalogColumn } from "./maxBinDefaults";

export type MaxBinCriteria = Pick<
  mBinSize,
  "MaxBinsSortOrder" | "MaxBinsSortField" | "MaxBinsLinkToAxis"
>;

export interface MaxBinSumField {
  property: string;
  title: string;
}

// The columns data-svc-rs sums for the legacy "Production" criteria.
const LEGACY_PRODUCTION_FIELD: Record<EntityKind, string> = {
  [EntityKind.Well]: "Performance.BOE.CurrentCalRate",
  [EntityKind.Facility]: "Performance.GasOutlet.CurrentCalRate"
};

const LEGACY_PRODUCTION_LABEL: Record<EntityKind, string> = {
  [EntityKind.Well]: "Production",
  [EntityKind.Facility]: "Gas Outlet"
};

export const LINK_TO_AXIS_LABEL = "Link to Axis";

export const getEntityCountLabel = (entityKind: EntityKind): string =>
  `${entityKind} Count`;

/**
 * The field a sum criteria ranks by. A saved field the catalogue no longer lists keeps
 * its raw key as the title; data-svc-rs ranks that bin by count instead.
 */
export function getMaxBinSumField(
  bin: Maybe<MaxBinCriteria>,
  entityKind: EntityKind,
  categoryList: Maybe<IGroupByListItem[]>
): Maybe<MaxBinSumField> {
  if (!bin || bin.MaxBinsLinkToAxis) return null;
  if (bin.MaxBinsSortOrder === "FieldSum" && bin.MaxBinsSortField) {
    const property = bin.MaxBinsSortField;
    return {
      property,
      title: findCatalogColumn(categoryList, property)?.title ?? property
    };
  }
  if (bin.MaxBinsSortOrder === "Production") {
    const property = LEGACY_PRODUCTION_FIELD[entityKind];
    const column = findCatalogColumn(categoryList, property);
    return column ? { property, title: column.title } : null;
  }
  return null;
}

export function getMaxBinCriteriaLabel(
  bin: Maybe<MaxBinCriteria>,
  entityKind: EntityKind,
  categoryList: Maybe<IGroupByListItem[]>
): string {
  if (bin?.MaxBinsLinkToAxis) return LINK_TO_AXIS_LABEL;
  const sumField = getMaxBinSumField(bin, entityKind, categoryList);
  if (sumField) return `Sum of ${sumField.title}`;
  if (bin?.MaxBinsSortOrder === "Production") return LEGACY_PRODUCTION_LABEL[entityKind];
  return getEntityCountLabel(entityKind);
}

const LINK_TO_AXIS_AGGREGATION: Partial<
  Record<CalculationMethod, MaxBinsSortAggregation>
> = {
  [CalculationMethod.Sum]: "Sum",
  [CalculationMethod.Average]: "Average",
  [CalculationMethod.Min]: "Min",
  [CalculationMethod.Max]: "Max"
};

/**
 * How Link to Axis aggregates the numeric axis field, or null when it ranks by the
 * count (a Count calculation, or no numeric field).
 */
export const getLinkToAxisAggregation = (
  method: Maybe<CalculationMethod>,
  numericAxisField: Maybe<string>
): Maybe<MaxBinsSortAggregation> =>
  // chart-svc reads a missing stacked bar calculation as Count.
  (numericAxisField && LINK_TO_AXIS_AGGREGATION[method ?? CalculationMethod.Count]) ||
  null;

/** What Link to Axis ranks by: the entity count, or the calculation of the numeric axis field. */
export const getLinkToAxisLabel = (
  method: Maybe<CalculationMethod>,
  numericAxisTitle: Maybe<string>,
  entityKind: EntityKind
): string => {
  const aggregation = getLinkToAxisAggregation(method, numericAxisTitle);
  return aggregation
    ? `${aggregation} of ${numericAxisTitle}`
    : getEntityCountLabel(entityKind);
};
